/**
 * Sequences you can see the reason for.
 *
 * A list of numbers on a page is a guessing game: 1, 3, 6, 10 means nothing
 * until somebody shows you the triangles. So every rule here comes with a
 * SHAPE — the dots that make up each term, arranged the way that explains why
 * the next number is the next number. Squares are squares. Triangles are
 * triangles. Doubling splits in two. The pattern is not stated; it is built.
 */

export type RuleId = 'add' | 'times' | 'square' | 'triangle' | 'fib';

export interface Rule {
  id: RuleId;
  label: string;
  /** What is happening, in a sentence a child could say. */
  line: string;
}

export const RULES: Rule[] = [
  { id: 'add', label: 'Add', line: 'add the same amount each time' },
  { id: 'times', label: 'Double', line: 'twice as many each time' },
  { id: 'square', label: 'Squares', line: 'a square of dots each time' },
  { id: 'triangle', label: 'Triangles', line: 'one more row each time' },
  { id: 'fib', label: 'Add the last two', line: 'add the two before it' },
];

export interface Options {
  /** For 'add' and 'times' — where it starts and how much it grows by. */
  start?: number;
  step?: number;
}

/** The nth term, counting n from 1. */
export function termAt(rule: RuleId, n: number, o: Options = {}): number {
  const start = o.start ?? 1;
  const step = o.step ?? 2;
  const index = Math.max(1, Math.floor(n));

  switch (rule) {
    case 'add':
      return start + step * (index - 1);
    case 'times':
      return start * step ** (index - 1);
    case 'square':
      return index * index;
    case 'triangle':
      // 1, 3, 6, 10 — each one is the row before it plus a longer new row.
      return (index * (index + 1)) / 2;
    case 'fib': {
      let a = 1;
      let b = 1;
      for (let i = 2; i < index; i++) {
        const next = a + b;
        a = b;
        b = next;
      }
      return index <= 2 ? 1 : b;
    }
  }
}

export function termsOf(rule: RuleId, count: number, o: Options = {}): number[] {
  return Array.from({ length: Math.max(0, count) }, (_, i) => termAt(rule, i + 1, o));
}

export interface Dot {
  x: number;
  y: number;
}

/**
 * Where the dots of the nth term go, in a small grid of its own. This is the
 * whole point of the toy: the arrangement is the explanation.
 */
export function dotsFor(rule: RuleId, n: number, o: Options = {}): Dot[] {
  const index = Math.max(1, Math.floor(n));
  const total = termAt(rule, index, o);
  const dots: Dot[] = [];

  if (rule === 'square') {
    for (let row = 0; row < index; row++) {
      for (let col = 0; col < index; col++) dots.push({ x: col, y: row });
    }
    return dots;
  }

  if (rule === 'triangle') {
    // Row r has r + 1 dots, so each new term is one longer row than the last.
    for (let row = 0; row < index; row++) {
      for (let col = 0; col <= row; col++) dots.push({ x: col, y: row });
    }
    return dots;
  }

  // Everything else stacks into a block as wide as it can sensibly be, so the
  // eye compares heights rather than counting.
  const width = Math.max(1, Math.min(10, Math.ceil(Math.sqrt(total))));
  for (let i = 0; i < Math.min(total, 400); i++) {
    dots.push({ x: i % width, y: Math.floor(i / width) });
  }
  return dots;
}

/** How wide and tall the nth term's arrangement is, for laying it out. */
export function extentOf(rule: RuleId, n: number, o: Options = {}): { w: number; h: number } {
  const dots = dotsFor(rule, n, o);
  let w = 0;
  let h = 0;
  for (const d of dots) {
    w = Math.max(w, d.x + 1);
    h = Math.max(h, d.y + 1);
  }
  return { w, h };
}

/**
 * The step from one term to the next, which for these rules is itself the
 * interesting thing: the gaps between triangle numbers are 2, 3, 4, 5.
 */
export function gapsOf(terms: number[]): number[] {
  const out: number[] = [];
  for (let i = 1; i < terms.length; i++) out.push(terms[i] - terms[i - 1]);
  return out;
}

/** True when the same gap repeats — the sequence goes up in equal steps. */
export function isSteady(terms: number[]): boolean {
  const gaps = gapsOf(terms);
  return gaps.length > 1 && gaps.every((g) => g === gaps[0]);
}
